import { DATASTRATEGY, EntityManager } from '@dch/entity-manager';
import { SessionStorageStrategy } from '@dch/entity-manager/lib/storage/strategies/session-storage.strategy';
import * as i0 from '@angular/core';
import { Injectable, Inject, Optional } from '@angular/core';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CustomerAccount } from '@dch/common-business-logic';
import * as i1 from '@dch/ng-common/src/lib/cookie';
import * as i2 from '@dch/ng-common/src/lib/environment';
import * as i3 from '@dch/ng-common/src/lib/dynatrace';

const UserEntity = {
    secure: true,
    disableCache: true,
    requestHeaders: [
        'language',
        'proximusContentType',
        'userId',
        'etag',
        'encryptionKey',
        'customHeaders',
    ],
    rel: 'us:user',
    url: '/rest/user/user',
    version: 'v2',
    parameters: {},
    dataStrategy: DATASTRATEGY.STORAGE_PROXY,
    storageStrategy: SessionStorageStrategy.ID,
};
const StorageUserEntity = {
    secure: true,
    disableCache: true,
    requestHeaders: [
        'language',
        'proximusContentType',
        'userId',
        'etag',
        'encryptionKey',
        'customHeaders',
    ],
    rel: 'us:user',
    version: 'v2',
    parameters: {},
    dataStrategy: DATASTRATEGY.STORAGE_ONLY,
    storageStrategy: SessionStorageStrategy.ID,
};
const ReviewerEntity = {
    requestHeaders: ['language', 'proximusContentType', 'userId', 'etag', 'encryptionKey'],
    secure: false,
    rel: 'us:reviewer',
    url: '/rest/user/user/reviewer{?msisdn}',
    version: 'v1',
    requiredJwts: ['x-application-authorization'],
    dataStrategy: DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS,
    storageStrategy: SessionStorageStrategy.ID,
};

class CustomerService {
    constructor(cookieService, environmentService, dynatraceService, isUserIdentifiedByDynatrace) {
        this.cookieService = cookieService;
        this.environmentService = environmentService;
        this.dynatraceService = dynatraceService;
        this.isUserIdentifiedByDynatrace = isUserIdentifiedByDynatrace;
        this.isTaggedByDynatrace = true;
        if (this.isUserIdentifiedByDynatrace != null) {
            this.isTaggedByDynatrace = this.isUserIdentifiedByDynatrace;
        }
    }
    getCustomerAccount() {
        if (this.customerAccount) {
            return of(this.customerAccount);
        }
        return EntityManager.getEntity(UserEntity).pipe(map((userEntity) => {
            this.customerAccount = new CustomerAccount(userEntity.value);
            this.tagUserToDynatrace();
            return this.customerAccount;
        }));
    }
    getReviewerRole(msisdn) {
        return this.reviewer
            ? of(this.reviewer)
            : EntityManager.getEntity(ReviewerEntity, { msisdn }).pipe(map((userEntity) => {
                this.reviewer = userEntity.value.reviewer;
                return this.reviewer;
            }));
    }
    getCustomerSubscriptionType(productBrand) {
        if (!productBrand) {
            return;
        }
        return ['prepaid', 'mcsautoreload', 'boneautoreload'].indexOf(productBrand) > -1
            ? 'prepaid'
            : 'postpaid';
    }
    isUserAuthenticatedWithMyProximus() {
        return this.getCurrentUserSessionCookieValue() !== undefined;
    }
    getMyProximusAuthenticatedCookieName() {
        if (this.environmentService.isProd || this.environmentService.isDR) {
            return 'iiamsid';
        }
        else if (this.environmentService.isUat) {
            return 'iiamsid-uat';
        }
        else {
            return 'iiamsid-itt';
        }
    }
    getCurrentUserSessionCookieValue() {
        const iIamCookie = this.getMyProximusAuthenticatedCookieName();
        const currentUserIIamCookieValue = this.cookieService.getCookie(iIamCookie);
        return currentUserIIamCookieValue;
    }
    tagUserToDynatrace() {
        if (this.dynatraceService.enabled && this.isTaggedByDynatrace) {
            this.dynatraceService.identifyUser(this.customerAccount.uniqueId);
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: CustomerService, deps: [{ token: i1.CookieService }, { token: i2.EnvironmentService }, { token: i3.PxDynatraceService }, { token: 'isUserIdentifiedByDynatrace', optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: CustomerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: CustomerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.CookieService }, { type: i2.EnvironmentService }, { type: i3.PxDynatraceService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['isUserIdentifiedByDynatrace']
                }, {
                    type: Optional
                }] }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CustomerService, ReviewerEntity, StorageUserEntity, UserEntity };
